"use client"; // This makes the component a Client Component
import React, { useEffect, useState } from 'react'
import { usePathname } from 'next/navigation';

const SITE = "https://www.ashlok.com";

// ---- Organization schema (site-wide / homepage) ----
const ORGANIZATION = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Ashlok Safe Earthing Electrode Limited",
  "url": `${SITE}/`,
  "logo": `${SITE}/og-image.jpg`,
  "foundingDate": "1999",
  "email": "earthing@ashlok.com",
  "telephone": "+91-94440-87356",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "58, SIDCO Industrial Estate, Sector-1, 3rd Street, North Phase, Ambattur",
    "addressLocality": "Chennai",
    "addressRegion": "Tamil Nadu",
    "postalCode": "600098",
    "addressCountry": "IN"
  },
  "sameAs": [
    "https://www.facebook.com/profile.php?id=100068589324482",
    "https://www.linkedin.com/company/8507004",
    "https://www.instagram.com/ashlok_safe_earthing"
  ]
};

// ---- Product data (name/category/description from approved page metadata) ----
const PRODUCTS = {
  "/ashlok-safe-earthing-electrodes": {
    name: "ASEEL Safe Earthing Electrode",
    category: "Earthing Electrode",
    description: "Maintenance-free chemical earthing electrodes (ASEEL) — composite carbon, alloy, zinc, PVD and copper-plated — compliant with IS 3043 and UL467, in 2m and 3m lengths.",
  },
  "/ashlok-composite-carbon-coating": {
    name: "Ashlok Composite Carbon Coated Earthing Electrode",
    category: "Earthing Electrode",
    description: "Ashlok's composite carbon-coated earthing rods designed for superior conductivity and durability. Reliable chemical earthing solutions for long-lasting safety.",
  },
  "/ashlok-carbo-form-conducto-casting": {
    name: "Ashlok Carbo-Form Conducto Cast Earthing Electrode",
    category: "Earthing Electrode",
    description: "Efficient earthing installation with Ashlok's Carbo-Form Conducto Cast technology. Trusted earth rod installation and grounding solutions.",
  },
  "/ashlok-copper-plated-electrodes": {
    name: "Ashlok Copper Plated Earthing Electrode",
    category: "Earthing Electrode",
    description: "High-quality copper-plated electrodes, copper ground rods and earthing rods from Ashlok, ensuring superior safety and reliability in grounding systems.",
  },
  "/ashlok-alloy-coated-electrodes": {
    name: "Ashlok Alloy Coated Earthing Electrode",
    category: "Earthing Electrode",
    description: "Ashlok's alloy-coated electrodes enhance your earthing system. Trusted earthing electrode manufacturer delivering durable and efficient solutions.",
  },
  "/ashlok-pvd-150-coated": {
    name: "Ashlok PVD-150 Coated Earthing Electrode",
    category: "Earthing Electrode",
    description: "Premium PVD-coated grounding electrodes from Ashlok. Reliable chemical earthing electrodes for long-lasting, low-resistance earthing systems.",
  },
  "/ashlok-zinc-coated-electrodes": {
    name: "Ashlok Zinc Coated (GI) Earthing Electrode",
    category: "Earthing Electrode",
    description: "Ashlok's zinc-coated electrodes and GI earthing rods. Expert in GI plate earthing and chemical earthing solutions.",
  },
  "/ashlok-galvanized-electrodes": {
    name: "Ashlok Galvanized Earthing Electrode",
    category: "Earthing Electrode",
    description: "Ashlok's galvanized electrodes for robust earthing systems. Leading chemical earthing manufacturer in Tamil Nadu and across India.",
  },
  "/ashlok-copper-bonded-rods": {
    name: "Copper Bonded Rod",
    category: "Earthing Electrode",
    description: "Copper bonded earthing rods to UL467 — 99.9% electrolytic copper on a high-tensile steel core, 100/250-micron coating, 14–38mm diameter, threaded with couplers.",
  },
  "/ashlok-earthing-accessories": {
    name: "Ashlok Earthing Accessories",
    category: "Earthing Accessories",
    description: "Ashlok's range of earthing accessories, including earth clamps, ground bars, copper busbars and more. Reliable solutions for all earthing needs.",
  },
  "/ashlok-earthpit-chamber-cover": {
    name: "Ashlok EarthPit Chamber Cover (EPCC)",
    category: "Earthing Accessories",
    description: "HDPE and glass-fibre earth pit chamber cover, UV-stabilised and high-strength, with zero scrap value (theft-deterrent), plug-and-play and maintenance-free.",
  },
  "/ashlok-exothermic-welding-kit": {
    name: "Exothermic Welding Kit",
    category: "Welding",
    description: "Exothermic welding kits for permanent, low-resistance earthing connections — graphite moulds and weld powder in a range of sizes.",
  },
  "/ashlok-baron-backfill-compound": {
    name: "Earthing Backfill Compound (BFC)",
    category: "Earthing Enhancing Compound",
    description: "Backfill compounds for earthing — Terraion, Zetalite, Conductolite, Humedite and Electrodite — that lower soil resistivity for low, stable earth resistance.",
  },
  "/ashlok-thor-safe-protection-lightning-rod": {
    name: "Thor ESE Lightning Arrester",
    category: "Lightning Arrester",
    description: "Thor ESE (early streamer emission) air terminal to NFC 17-102 in SS304, giving a larger protection radius for large and open sites.",
  },
  "/ashlok-zeus-slpr": {
    name: "Zeus ESE Lightning Arrester (SLPR)",
    category: "Lightning Arrester",
    description: "Zeus SLPR ESE lightning arrester to NFC 17-102 — an active air terminal giving a wide protection radius from a single mast for industrial and open sites.",
  },
  "/ashlok-air-terminal": {
    name: "Ashlok Air Terminal (Conventional Lightning Arrester)",
    category: "Lightning Protection",
    description: "Ashlok's air terminal solutions, including rod lightning systems, lightning absorbers and conventional lightning arresters for comprehensive protection.",
  },
  "/ashlok-franklyn-penta": {
    name: "Ashlok Franklyn Penta Lightning Conductor",
    category: "Lightning Protection",
    description: "Ashlok's lightning conductor solutions, specializing in protection system installation and lightning rod systems for unmatched safety.",
  },
  "/ashlok-surge-protection-device": {
    name: "Surge Protection Device",
    category: "Surge Protector",
    description: "Ashlok surge protection devices — Class B & C AC (EN 61643-11), solar PV DC and street-light SPDs — protecting panels and electronics from voltage surges.",
  },
  "/ashlok-alice-digital": {
    name: "Ashlok Alice Digital Lightning Strike Counter",
    category: "Lightning Protection Accessory",
    description: "Track lightning activity with Ashlok's lightning strike counters. Part of Ashlok's lightning arrester range for complete safety.",
  },
};

// ---- Service schema pages ----
const SERVICES = {
  "/ashlok-lightning-protection-systems": {
    name: "Lightning Protection System",
    description: "Conventional and ESE lightning protection systems — air terminals, down conductors and earthing — with design, supply, installation and commissioning pan-India.",
  },
  "/ashlok-conventional-lightning-protection": {
    name: "Conventional Lightning Protection",
    description: "Franklin-rod and mesh lightning conductor systems to IS/IEC standards — air termination, down conductors and low-resistance earthing for buildings and plants.",
  },
};

// ---- FAQs (2 buyer questions per page; facts only) ----
const FAQS = {
  "/ashlok-safe-earthing-electrodes": [
    { q: "What is a maintenance-free chemical earthing electrode?", a: "It is a factory-filled earthing electrode (ASEEL range) that maintains a low, stable earth resistance without periodic watering, compliant with IS 3043 and UL467, available in 2m and 3m lengths." },
    { q: "Which coating options does Ashlok offer?", a: "Ashlok manufactures composite carbon, alloy, zinc, PVD and copper-plated earthing electrodes to suit different soil and budget requirements." },
  ],
  "/ashlok-surge-protection-device": [
    { q: "Which types of surge protection devices does Ashlok make?", a: "Ashlok offers Class B and Class C AC SPDs to EN 61643-11, plus solar PV DC SPDs and street-light SPDs." },
    { q: "Why do I need a surge protection device?", a: "An SPD diverts transient overvoltages from lightning or switching to earth, protecting panels, electronics and equipment from damage." },
  ],
  "/ashlok-lightning-protection-systems": [
    { q: "What does a complete lightning protection system include?", a: "Air terminals, down conductors and a low-resistance earthing system, designed to safely capture and dissipate a lightning strike." },
    { q: "Does Ashlok install lightning protection systems?", a: "Yes — Ashlok provides design, supply, installation and commissioning of conventional and ESE lightning protection systems pan-India." },
  ],
  "/ashlok-conventional-lightning-protection": [
    { q: "What is conventional lightning protection?", a: "A Franklin-rod or mesh system with air termination, down conductors and low-resistance earthing to IS/IEC standards, protecting buildings and plants." },
    { q: "How is it different from ESE protection?", a: "Conventional systems use passive air terminals and multiple down conductors; ESE systems use an active early-streamer-emission terminal for a larger protection radius from a single mast." },
  ],
  "/ashlok-thor-safe-protection-lightning-rod": [
    { q: "What standard does the Thor ESE lightning arrester meet?", a: "The Thor ESE (early streamer emission) air terminal is designed to NFC 17-102 and is built in SS304." },
    { q: "Where is an ESE arrester most useful?", a: "On large or open sites, because a single mast provides a larger protection radius than conventional air terminals." },
  ],
  "/ashlok-zeus-slpr": [
    { q: "What is the Zeus SLPR?", a: "Zeus is an ESE (early streamer emission) lightning arrester to NFC 17-102 — an active air terminal giving a wide protection radius from a single mast." },
    { q: "Which sites suit the Zeus SLPR?", a: "Industrial plants and open sites where a single mast can protect a large area." },
  ],
  "/ashlok-exothermic-welding-kit": [
    { q: "What is an exothermic welding kit used for?", a: "It makes permanent, low-resistance earthing connections using a graphite mould and weld powder, with no external power source." },
    { q: "What sizes are available?", a: "Ashlok supplies graphite moulds and weld powder in a range of sizes to suit different conductor and rod combinations." },
  ],
  "/ashlok-earthpit-chamber-cover": [
    { q: "What is the Ashlok EarthPit Chamber Cover made of?", a: "The EPCC is made of HDPE with glass fibre, UV-stabilised for outdoor use, offering high strength and a maintenance-free, plug-and-play design." },
    { q: "Is it theft-resistant?", a: "Yes — unlike metal covers it has zero scrap value, which deters theft." },
  ],
  "/ashlok-copper-bonded-rods": [
    { q: "What standard do Ashlok copper bonded rods meet?", a: "They are manufactured to UL467, using 99.9% electrolytic copper molecularly bonded to a high-tensile steel core." },
    { q: "What sizes and coatings are available?", a: "Diameters from 14 to 38mm with 100 or 250 micron copper coating, threaded with couplers for extension." },
  ],
  "/ashlok-baron-backfill-compound": [
    { q: "What is an earthing backfill compound?", a: "A conductive compound placed around the electrode to lower soil resistivity and maintain a low, stable earth resistance." },
    { q: "Which backfill compounds does Ashlok offer?", a: "Terraion, Zetalite, Conductolite, Humedite and Electrodite, selected to suit the soil condition." },
  ],
};

const breadcrumb = (pathname, name) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE}/home` },
    { "@type": "ListItem", "position": 2, "name": "Products", "item": `${SITE}/product` },
    { "@type": "ListItem", "position": 3, "name": name, "item": `${SITE}${pathname}` }
  ]
});

const productSchema = (pathname, p) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  "name": p.name,
  "brand": { "@type": "Brand", "name": "Ashlok" },
  "manufacturer": { "@type": "Organization", "name": "Ashlok Safe Earthing Electrode Limited" },
  "category": p.category,
  "image": `${SITE}/og-image.jpg`,
  "description": p.description,
  "url": `${SITE}${pathname}`
});

const serviceSchema = (pathname, s) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "name": s.name,
  "serviceType": s.name,
  "description": s.description,
  "provider": { "@type": "Organization", "name": "Ashlok Safe Earthing Electrode Limited" },
  "areaServed": { "@type": "Place", "name": "India" },
  "url": `${SITE}${pathname}`
});

const faqSchema = (pathname) => {
  const items = FAQS[pathname];
  if (!items) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": items.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a }
    }))
  };
};

const generateSchema = (pathname) => {
  const out = [];

  if (PRODUCTS[pathname]) {
    out.push(productSchema(pathname, PRODUCTS[pathname]));
    out.push(breadcrumb(pathname, PRODUCTS[pathname].name));
  } else if (SERVICES[pathname]) {
    out.push(serviceSchema(pathname, SERVICES[pathname]));
    out.push(breadcrumb(pathname, SERVICES[pathname].name));
  } else {
    switch (pathname) {
      case '/':
      case '/home':
        out.push(ORGANIZATION);
        break;
      case '/ourheritage':
        out.push({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Ashlok Safe Earthing Electrode Limited",
          "description": "Leading manufacturer of earthing and lightning protection products.",
          "url": `${SITE}/ourheritage`,
          "foundingDate": "1999",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "58, SIDCO Industrial Estate, 3rd Street, North Phase, Ambattur",
            "addressLocality": "Chennai",
            "addressRegion": "Tamil Nadu",
            "postalCode": "600098",
            "addressCountry": "IN"
          },
          "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+91-94440-87356",
            "contactType": "Customer Service"
          }
        });
        break;
      case '/ourservices':
        out.push({
          "@context": "https://schema.org",
          "@type": "Service",
          "serviceType": "Earthing & Lightning Protection System",
          "provider": { "@type": "Organization", "name": "Ashlok Safe Earthing Electrode Limited" },
          "areaServed": { "@type": "Place", "name": "India" }
        });
        break;
      case '/contact':
        out.push({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "Ashlok Safe Earthing Electrode Limited - Lightning Protection Experts",
          "url": `${SITE}/contact`,
          "logo": `${SITE}/og-image.jpg`,
          "image": `${SITE}/og-image.jpg`,
          "description": "Ashlok specializes in lightning protection systems and earthing solutions, providing high-quality products and installation services PAN India.",
          "telephone": "+91-44-2625 4757",
          "email": "earthing@ashlok.com",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "No. 58 | 3rd Street | North Phase | SIDCO Industrial Estate | Ambattur",
            "addressLocality": "Chennai",
            "addressRegion": "Tamil Nadu",
            "postalCode": "600098",
            "addressCountry": "IN"
          },
          "openingHours": "Mo-Sa 09:00-18:00",
          "geo": { "@type": "GeoCoordinates", "latitude": "13.108952320050774", "longitude": "80.16645318387117" },
          "areaServed": ["India"],
          "sameAs": [
            "https://www.facebook.com/profile.php?id=100068589324482",
            "https://www.linkedin.com/company/8507004",
            "https://www.instagram.com/ashlok_safe_earthing"
          ],
          "hasMap": "https://maps.app.goo.gl/CL7Gtg1jtDgZN4aR7"
        });
        break;
      default:
        out.push(ORGANIZATION);
    }
  }

  const faq = faqSchema(pathname);
  if (faq) out.push(faq);

  return out.length === 1 ? out[0] : out;
};

const HeaderInner = () => {
  const SITE_URL = SITE;
  const pathname = usePathname() || "/";

  // Self-referencing canonical: base host + current path (no trailing slash except root).
  const path = pathname === "/" ? "" : pathname.replace(/\/$/, "");

  // Reflect an in-page section (e.g. the Our Services tabs) via the URL hash.
  const [hash, setHash] = useState("");
  useEffect(() => {
    const update = () => setHash(window.location.hash || "");
    update();
    window.addEventListener("hashchange", update);
    return () => window.removeEventListener("hashchange", update);
  }, [pathname]);

  const canonicalUrl = `${SITE_URL}${path}${hash}` || SITE_URL;
  const schema = generateSchema(pathname);

  return (
    <>
      <link rel="canonical" href={canonicalUrl} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      />
    </>
  );
};

export default HeaderInner;
